import React, { useState, useEffect } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { X, Save, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';

const EditPaymentModal = ({ payment, onClose }) => {
  const { updatePayment } = useFinance();
  const [formData, setFormData] = useState({
    name: '',
    category: 'suscripciones',
    amount: '',
    paymentDay: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    installments: '',
    payoffAmount: '',
    canPayOff: false,
    observations: '',
    useExactDates: true
  });
  const [showObservations, setShowObservations] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (payment) {
      const hasExactDates = payment.startDate && payment.endDate;
      const useExactDates = hasExactDates || payment.useExactDates;

      setFormData({
        name: payment.name || '',
        category: payment.category || 'suscripciones',
        amount: payment.amount || '',
        paymentDay: payment.paymentDay || '',
        startDate: payment.startDate || new Date().toISOString().split('T')[0],
        endDate: payment.endDate || '',
        installments: payment.installments || '',
        payoffAmount: payment.payoffAmount || '',
        canPayOff: payment.canPayOff || false,
        observations: payment.observations || '',
        useExactDates: useExactDates,
        startMonth: payment.startMonth || new Date().getMonth() + 1
      });
      setShowObservations(!!payment.observations);
      setShowAdvanced(payment.installments > 0 || payment.payoffAmount > 0 || !!payment.observations);
    }
  }, [payment]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'useExactDates') {
        if (checked) {
          newData.startMonth = undefined;
          newData.installments = '';
        } else {
          newData.startDate = '';
          newData.endDate = '';
        }
      }

      if (name === 'startDate' && newData.installments && newData.installments > 0) {
        const startDate = new Date(value);
        const monthsToAdd = parseInt(newData.installments);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + monthsToAdd);
        newData.endDate = endDate.toISOString().split('T')[0];
      }

      if (name === 'installments' && newData.startDate && value > 0) {
        const startDate = new Date(newData.startDate);
        const monthsToAdd = parseInt(value);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + monthsToAdd);
        newData.endDate = endDate.toISOString().split('T')[0];
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('El nombre del pago es obligatorio.');
      return;
    }

    setSaving(true);

    const updatedPayment = {
      name: formData.name.trim(),
      category: formData.category,
      amount: parseFloat(formData.amount) || 0,
      paymentDay: parseInt(formData.paymentDay) || 0,
      payoffAmount: parseFloat(formData.payoffAmount) || 0,
      canPayOff: formData.canPayOff,
      observations: formData.observations.trim(),
      useExactDates: formData.useExactDates
    };

    if (formData.useExactDates) {
      updatedPayment.startDate = formData.startDate;
      updatedPayment.endDate = formData.endDate;
      updatedPayment.installments = parseInt(formData.installments) || 0;
    } else {
      updatedPayment.startMonth = parseInt(formData.startMonth) || new Date().getMonth() + 1;
      updatedPayment.installments = parseInt(formData.installments) || 0;
    }

    await updatePayment(payment.id, updatedPayment);
    setSaving(false);
    onClose();
  };

  const generateDayOptions = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(
        <option key={i} value={i}>
          Día {i}
        </option>
      );
    }
    return days;
  };

  if (!payment) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content animate-slide-up md:animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">✏️ Editar Pago</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="form-group mb-0">
                <label htmlFor="editName" className="form-label">
                  📝 Nombre *
                </label>
                <input
                  type="text"
                  id="editName"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ej: Netflix"
                  className="input-field"
                  required
                />
              </div>

              <div className="form-group mb-0">
                <label htmlFor="editCategory" className="form-label">
                  🏷️ Categoría
                </label>
                <select
                  id="editCategory"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="suscripciones">Suscripciones</option>
                  <option value="casa">Pagos de Casa</option>
                  <option value="plazos">Pagos a Plazo</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="form-group mb-0">
                <label htmlFor="editAmount" className="form-label">
                  💰 Cantidad (€) *
                </label>
                <input
                  type="number"
                  id="editAmount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Ej: 15.99"
                  step="0.01"
                  className="input-field"
                  required
                />
              </div>

              <div className="form-group mb-0">
                <label htmlFor="editDay" className="form-label">
                  📅 Día de Cobro *
                </label>
                <select
                  id="editDay"
                  name="paymentDay"
                  value={formData.paymentDay}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {generateDayOptions()}
                </select>
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors mb-4"
            >
              <span className="text-sm font-medium text-gray-700">
                ⚙️ Opciones avanzadas
              </span>
              {showAdvanced ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>

            {/* Advanced Options */}
            <div className={`overflow-hidden transition-all duration-300 ${showAdvanced ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    name="useExactDates"
                    checked={formData.useExactDates}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">
                    🗓️ Usar fechas exactas
                  </span>
                </label>

                {formData.useExactDates ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group mb-0">
                      <label htmlFor="editStartDate" className="form-label">
                        Fecha de Inicio
                      </label>
                      <input
                        type="date"
                        id="editStartDate"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className="input-field"
                        required
                      />
                    </div>

                    <div className="form-group mb-0">
                      <label htmlFor="editEndDate" className="form-label">
                        Fecha de Fin
                      </label>
                      <input
                        type="date"
                        id="editEndDate"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        className="input-field"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-group mb-0">
                    <label htmlFor="editStartMonth" className="form-label">
                      Mes de Inicio
                    </label>
                    <select
                      id="editStartMonth"
                      name="startMonth"
                      value={formData.startMonth || new Date().getMonth() + 1}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                      ].map((month, idx) => (
                        <option key={idx + 1} value={idx + 1}>{month}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group mb-0">
                  <label htmlFor="editInstallments" className="form-label">
                    📊 Cuotas (0 = fijo)
                  </label>
                  <input
                    type="number"
                    id="editInstallments"
                    name="installments"
                    value={formData.installments}
                    onChange={handleInputChange}
                    placeholder="Ej: 12"
                    min="0"
                    className="input-field"
                  />
                </div>

                <div className="form-group mb-0">
                  <label htmlFor="editPayoffAmount" className="form-label">
                    💳 Pago único (€)
                  </label>
                  <input
                    type="number"
                    id="editPayoffAmount"
                    name="payoffAmount"
                    value={formData.payoffAmount}
                    onChange={handleInputChange}
                    placeholder="Ej: 150"
                    step="0.01"
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="canPayOff"
                    checked={formData.canPayOff}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 text-sm">Se puede pagar de una vez</span>
                </label>

                <button
                  type="button"
                  onClick={() => setShowObservations(!showObservations)}
                  className="btn btn-sm btn-secondary"
                >
                  {showObservations ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showObservations ? 'Ocultar notas' : 'Ver notas'}
                </button>
              </div>

              {showObservations && (
                <div className="form-group mb-0">
                  <label htmlFor="editObservations" className="form-label">
                    📝 Observaciones
                  </label>
                  <textarea
                    id="editObservations"
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    placeholder="Notas adicionales..."
                    rows="2"
                    className="input-field resize-none"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1 sm:flex-none"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className="btn btn-success flex-1 sm:flex-none"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal;